import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LANDING } from "@/constants/testIds";

// Featured coaching categories. Each card links straight to the marketplace
// with a pre-applied `specialty` filter so a click takes the visitor straight
// to relevant coaches.
//
// Layout intent (marketplace-style, not a plain list):
//   - Row 1: 2 tall hero cards (boxing, swimming)
//   - Row 2: 3 shorter cards (cooking, music, tennis)
// The mix of tall + short breaks the grid rhythm without adding chrome.
const CATEGORIES = [
  {
    slug: "boxing",
    label: "Boxing",
    tagline: "Sharpen power, footwork, and defence",
    // Unsplash: gloves in a dark gym — high contrast, dramatic
    image: "https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=1400&h=1100&fit=crop&q=80",
    tall: true,
  },
  {
    slug: "swimming",
    label: "Swimming",
    tagline: "Stroke technique, endurance, race prep",
    // Unsplash: underwater swimmer
    image: "https://images.unsplash.com/photo-1530549387789-4c1017266635?w=1400&h=1100&fit=crop&q=80",
    tall: true,
  },
  {
    slug: "cooking",
    label: "Cooking",
    tagline: "Home chef → pro-level technique",
    // Unsplash: chef plating — warm tones
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=900&h=700&fit=crop&q=80",
    tall: false,
  },
  {
    slug: "music",
    label: "Music",
    tagline: "1-on-1 lessons in any instrument",
    // Unsplash: guitar close-up
    image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=900&h=700&fit=crop&q=80",
    tall: false,
  },
  {
    slug: "tennis",
    label: "Tennis",
    tagline: "Grip, footwork, match strategy",
    // Unsplash: player on a court
    image: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=900&h=700&fit=crop&q=80",
    tall: false,
  },
];

function CategoryCard({ item, index }) {
  const navigate = useNavigate();
  const goto = () => navigate(`/marketplace?specialty=${encodeURIComponent(item.label)}`);
  return (
    <motion.button
      type="button"
      onClick={goto}
      data-testid={LANDING.categoryCard(item.slug)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden rounded-3xl text-left ${
        item.tall ? "aspect-[4/5] md:aspect-[3/4]" : "aspect-[4/3] md:aspect-[5/4]"
      }`}
      aria-label={`Browse ${item.label} coaches`}
    >
      {/* Background image */}
      <img
        src={item.image}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
      />
      {/* Gradient overlay — always visible, deepens on hover */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10 transition-opacity duration-500 group-hover:from-black/80" />
      {/* Label + tagline */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-white">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-medium text-3xl md:text-4xl leading-none tracking-[-0.025em] drop-shadow-sm">
              {item.label}
            </h3>
            <p className="mt-2 text-[13.5px] md:text-[14.5px] text-white/85 leading-snug max-w-[26ch]">
              {item.tagline}
            </p>
          </div>
          <span
            className="shrink-0 h-11 w-11 rounded-full bg-white/95 text-slate-900 inline-flex items-center justify-center translate-x-1 translate-y-1 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-500"
            aria-hidden
          >
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </motion.button>
  );
}

export default function Categories() {
  const [heroA, heroB, ...rest] = CATEGORIES;
  return (
    <section
      id="categories"
      data-testid={LANDING.categories}
      className="py-24 md:py-32 border-t border-slate-100"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12 md:mb-16 flex items-end justify-between gap-6 flex-wrap"
        >
          <div>
            <div className="eyebrow">§ Explore</div>
            <h2 className="mt-4 font-display font-medium text-[36px] sm:text-[44px] md:text-[52px] leading-[1.02] tracking-[-0.035em] text-slate-900 max-w-[18ch]">
              Coaches for whatever you're chasing.
            </h2>
          </div>
          <p className="text-slate-500 max-w-[36ch] text-[15px] leading-relaxed">
            Pick a category to jump straight to verified independent coaches you can book today.
          </p>
        </motion.div>

        {/* Row 1 — two tall hero cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          <CategoryCard item={heroA} index={0} />
          <CategoryCard item={heroB} index={1} />
        </div>
        {/* Row 2 — three shorter cards */}
        <div className="mt-4 md:mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {rest.map((item, i) => (
            <CategoryCard key={item.slug} item={item} index={i + 2} />
          ))}
        </div>
      </div>
    </section>
  );
}
